<template>
  <div class="cr-page">
    <main class="cr-content" id="main-content">
      <header class="cr-header">
        <div class="cr-eyebrow">Complete your registration</div>
        <h1 class="cr-title">{{ event.name || 'Continue registration' }}</h1>
        <div v-if="attendee.email" class="cr-identity">
          Finishing registration for
          <strong>{{ attendeeName }}</strong>
          <span class="cr-identity-email">· {{ attendee.email }}</span>
        </div>
      </header>

      <div v-if="loading" class="cr-loading">Loading…</div>

      <template v-else>
        <div v-if="currentStep !== 'done'" class="topbar">
          <lp-stepper :steps="stepDef" :active-id="currentStep" />
          <span class="step-num-label">Step {{ stepNum }} of {{ stepDef.length }}</span>
        </div>

        <div v-if="errorMessage" class="reg-error-banner">{{ errorMessage }}</div>

        <!-- ============ STEP: INFORMATION ============ -->
        <section v-show="currentStep === 'information'" class="step-container">
          <div class="step-header">
            <h2 class="step-h">Your information</h2>
            <div class="sub">Complete your details to finish registering for this event.</div>
          </div>

          <ValidationObserver ref="infoForm" tag="div">
            <div id="attendee-cards">
              <lp-attendee-card
                :index="0"
                :ticket-name="attendee.ticket_name || 'Your registration'"
                :is-purchaser="true"
                :total-slots="1"
                :schema="fields"
                :values="values"
                @field-input="onFieldInput"
              />
            </div>
          </ValidationObserver>

          <div class="step-footer">
            <div></div>
            <div class="footer-actions">
              <button class="btn-primary" :disabled="busy" @click="continueFromInformation">
                {{ agendaGroupAvailable ? 'Continue to sessions' : 'Finish registration' }}
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
            </div>
          </div>
        </section>

        <!-- ============ STEP: SESSIONS ============ -->
        <section v-show="currentStep === 'sessions'" class="step-container">
          <div class="step-header">
            <h2 class="step-h">Build your agenda</h2>
            <div class="sub">Pick one session per time block. Blocks that overlap a selection are disabled.</div>
          </div>

          <div id="picker-content">
            <div v-if="!sessionDays.length" class="cr-empty">No sessions to select.</div>
            <div v-for="(day, di) in sessionDays" :key="di" class="day-section">
              <div class="day-header"><h2>{{ day.date }}</h2></div>
              <lp-session-card
                v-for="group in day.sessions"
                :key="group.group_id"
                :group="group"
                :pick="sessionPicks[group.group_id] || ''"
                :disabled="isGroupBlocked(group)"
                @pick="onPick"
              />
            </div>
          </div>

          <div class="step-footer">
            <button v-if="startStep !== 'sessions'" class="btn-outline" :disabled="busy" @click="showStep('information')">
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>
              Back
            </button>
            <div v-else></div>
            <div class="footer-progress">
              <span><strong>{{ decidedCount }} of {{ requiredCount }}</strong> blocks selected</span>
            </div>
            <div class="footer-actions">
              <button class="btn-primary" :disabled="busy || !sessionsComplete" @click="continueFromSessions">
                Finish registration
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
            </div>
          </div>
        </section>

        <!-- ============ DONE ============ -->
        <section v-show="currentStep === 'done'" class="step-container">
          <div class="confirm">
            <div class="confirm-icon">
              <svg aria-hidden="true" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7"/></svg>
            </div>
            <h2>Registration complete</h2>
            <div class="confirm-sub">Your registration details have been saved.</div>
          </div>
        </section>
      </template>
    </main>
  </div>
</template>

<script>
import { http } from '../../api/http'
import continueAPI from '../../api/continueAPI'

import lpStepper from '../registration/components/lp-stepper.vue'
import lpAttendeeCard from '../registration/components/lp-attendee-card.vue'
import lpSessionCard from '../registration/components/lp-session-card.vue'

export default {
  name: 'continue-registration-page',
  components: { lpStepper, lpAttendeeCard, lpSessionCard },
  props: {
    pathname: { type: String, required: true },
    csrfToken: { type: String, default: '' },
    token: { type: String, required: true },
    // 'information' (default) or 'sessions' — the agenda-group entry opens the
    // page straight on the sessions step (the info step is already done).
    startStep: { type: String, default: 'information' },
  },
  data() {
    return {
      loading: true,
      busy: false,
      errorMessage: '',
      currentStep: 'information',

      event: {},
      attendee: {},
      fields: [],
      validation: {},
      agendaGroupAvailable: false,
      redirectBack: null,

      values: {},

      sessions: { days: [], selected_session_ids: [] },
      sessionPicks: {},
    }
  },
  computed: {
    attendeeName() {
      return [this.attendee.first_name, this.attendee.last_name].filter(Boolean).join(' ')
    },
    stepDef() {
      if (this.startStep === 'sessions') return [{ id: 'sessions', label: 'Sessions' }]
      const steps = [{ id: 'information', label: 'Information' }]
      if (this.agendaGroupAvailable) steps.push({ id: 'sessions', label: 'Sessions' })
      return steps
    },
    stepNum() {
      const i = this.stepDef.findIndex(s => s.id === this.currentStep)
      return i >= 0 ? i + 1 : 1
    },
    sessionDays() {
      return this.sessions.days || []
    },
    // Groups the attendee must still decide (not blocked by another selection).
    requiredCount() {
      return this.sessionDays.reduce((sum, d) => sum + d.sessions.filter(g => !this.isGroupBlocked(g)).length, 0)
    },
    decidedCount() {
      // A block is "decided" once it has any pick — a session id OR 'skip'
      // (free time). Only '' / absent counts as undecided.
      const decidedGroups = new Set(
        Object.keys(this.sessionPicks).filter(gid => this.sessionPicks[gid])
      )
      let count = 0
      this.sessionDays.forEach(d => d.sessions.forEach(g => {
        if (!this.isGroupBlocked(g) && decidedGroups.has(String(g.group_id))) count += 1
      }))
      return count
    },
    // Every non-blocked group must be decided; skipping a block counts as decided.
    sessionsComplete() {
      return this.requiredCount === 0 || this.decidedCount === this.requiredCount
    },
  },
  created() {
    if (this.csrfToken) http.setCsrf(this.csrfToken)
    this.bootstrap()
  },
  methods: {
    async bootstrap() {
      this.loading = true
      try {
        const data = await continueAPI.bootstrap(this.pathname, this.token)
        this.event = data.event || {}
        this.attendee = data.attendee || {}
        this.fields = (data.form && data.form.fields) || []
        this.validation = data.validation || {}
        this.agendaGroupAvailable = !!data.agenda_group_available
        this.redirectBack = data.redirect_back || null
        this.initValues()

        // Agenda-group entry: skip the (already-done) info step.
        if (this.startStep === 'sessions') {
          if (this.agendaGroupAvailable) await this.loadSessions()
          if (this.agendaGroupAvailable) this.currentStep = 'sessions'
          else this.finish()
        }
      } catch (e) {
        this.errorMessage = e.message || 'This registration link is invalid or has expired.'
      } finally {
        this.loading = false
      }
    },
    initValues() {
      const v = {}
      this.fields.forEach(f => {
        if (f.type === 'static') return
        if (f.type === 'checkbox') v[f.name] = Array.isArray(f.value) ? f.value.slice() : []
        else v[f.name] = f.value != null ? f.value : ''
      })
      this.values = v
    },
    onFieldInput({ name, value }) {
      this.$set(this.values, name, value)
    },

    buildInformationPayload() {
      const out = { token: this.token }
      this.fields.forEach(f => {
        if (f.type === 'static') return
        let value = this.values[f.name]
        if (f.type === 'checkbox') {
          // Single consent checkboxes post a scalar in the legacy form; multi
          // groups post an array. Match that so the legacy validator is happy.
          if ((f.options || []).length <= 1) value = Array.isArray(value) && value.length ? value[0] : ''
          else value = Array.isArray(value) ? value : []
        }
        out[f.name] = value
      })
      return out
    },
    async continueFromInformation() {
      const ok = await this.$refs.infoForm.validate()
      if (!ok) { this.$nextTick(() => this.scrollToFirstError()); return }

      this.busy = true
      this.errorMessage = ''
      try {
        const data = await continueAPI.updateAttendee(this.pathname, this.buildInformationPayload())
        this.agendaGroupAvailable = !!data.agenda_group_available
        if (data.redirect_back) this.redirectBack = data.redirect_back

        if (this.agendaGroupAvailable) {
          await this.loadSessions()
          this.showStep('sessions')
        } else {
          this.finish()
        }
      } catch (e) {
        if (e.errors) {
          this.$refs.infoForm.setErrors(this.mapServerErrors(e.errors))
          this.$nextTick(() => this.scrollToFirstError())
        } else {
          this.errorMessage = e.message || 'Could not save your information. Please try again.'
        }
      } finally {
        this.busy = false
      }
    },

    async loadSessions() {
      const data = await continueAPI.fetchSessions(this.pathname, this.token)
      this.sessions = { days: data.days || [], selected_session_ids: data.selected_session_ids || [] }
      // If nothing turned out to be selectable, don't strand the attendee here.
      if (!this.sessions.days.length) { this.agendaGroupAvailable = false; return }
      this.initSessionPicks()
    },
    initSessionPicks() {
      const selected = (this.sessions.selected_session_ids || []).map(Number)
      const picks = {}
      this.sessionDays.forEach(d => d.sessions.forEach(g => {
        const hit = (g.items || []).find(it => selected.includes(Number(it.id)))
        if (hit) picks[g.group_id] = hit.id
      }))
      this.sessionPicks = picks
    },
    isGroupBlocked(group) {
      const realPickedGroupIds = Object.keys(this.sessionPicks)
        .filter(gid => this.sessionPicks[gid] && this.sessionPicks[gid] !== 'skip')
      return (group.concurrent_groups || []).some(cg => realPickedGroupIds.includes(cg))
    },
    onPick({ groupId, sessionId }) {
      this.$set(this.sessionPicks, groupId, sessionId)
      if (sessionId !== 'skip') {
        this.$nextTick(() => {
          this.sessionDays.forEach(d => d.sessions.forEach(g => {
            if (g.group_id !== groupId && this.isGroupBlocked(g) && this.sessionPicks[g.group_id]) {
              this.$delete(this.sessionPicks, g.group_id)
            }
          }))
        })
      }
    },
    async continueFromSessions() {
      const ids = Object.values(this.sessionPicks).filter(v => v && v !== 'skip')
      this.busy = true
      this.errorMessage = ''
      try {
        await continueAPI.updateSessions(this.pathname, { token: this.token, sessions: ids })
        this.finish()
      } catch (e) {
        this.errorMessage = e.message || 'Could not save your sessions. Please try again.'
      } finally {
        this.busy = false
      }
    },

    finish() {
      if (this.redirectBack) {
        window.location.href = this.redirectBack
        return
      }
      this.showStep('done')
    },
    showStep(name) {
      this.currentStep = name
      this.errorMessage = ''
      // Same as the registration page: scroll only after Vue has swapped the
      // v-show steps (so the document height has settled), aim at the step
      // block rather than the document top, and jump instead of animating —
      // a smooth scroll from the bottom of a long form is slow on a phone and
      // any touch aborts it midway.
      this.$nextTick(() => {
        const anchor = this.$el.querySelector('.cr-content')
        const top = anchor ? anchor.getBoundingClientRect().top + window.scrollY : 0
        window.scrollTo({ top: Math.max(0, top), behavior: 'auto' })
      })
    },
    mapServerErrors(errors) {
      // Server keys errors by field name; ValidationProvider vids are `name-0`.
      const mapped = {}
      Object.keys(errors).forEach(key => { mapped[`${key}-0`] = errors[key] })
      return mapped
    },
    scrollToFirstError() {
      const el = this.$el.querySelector('.field-error, .lp-input-error')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
  },
}
</script>

<style lang="scss" scoped>
.cr-page {
  min-height: 100vh;
  background: var(--bg);
}
.cr-content {
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 24px 80px;
}
.cr-header { margin-bottom: 24px; }
.cr-eyebrow {
  font-size: var(--fs-label);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  margin-bottom: 6px;
}
.cr-title {
  // one-off display size, kept literal so desktop is untouched
  font-size: 26px;
  font-weight: 600;
  color: var(--brand-deep);
  margin: 0 0 8px;
}
.cr-identity {
  font-size: var(--fs-body);
  color: var(--text-secondary);
}
.cr-identity-email { color: var(--text-tertiary); }
.cr-loading { padding: 60px 0; text-align: center; color: var(--text-tertiary); }
.cr-empty { padding: 30px; text-align: center; color: var(--text-tertiary); font-size: var(--fs-body); }

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 0.5px solid var(--border);
}
.step-num-label {
  font-size: var(--fs-label);
  color: var(--text-tertiary);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}
.reg-error-banner {
  margin-bottom: 16px;
  background: #fdecea;
  color: #b3261e;
  border: 0.5px solid #f5c2c0;
  border-radius: var(--radius-md);
  padding: 12px 16px;
  font-size: var(--fs-body);
}

.step-header { margin-bottom: 20px; }
.step-h {
  font-size: var(--fs-h5);
  font-weight: 600;
  color: var(--brand-deep);
  margin: 0 0 4px;
}
.step-header .sub { font-size: var(--fs-body); color: var(--text-secondary); }

.day-section { margin-bottom: 28px; }
.day-header h2 {
  font-size: var(--fs-body-lg);
  font-weight: 600;
  color: var(--brand-deep);
  margin: 0 0 12px;
}

.step-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 0.5px solid var(--border);
}
.footer-progress { font-size: var(--fs-small); color: var(--text-secondary); }
.footer-actions { display: flex; gap: 12px; }

.btn-primary,
.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--fs-body);
  font-weight: 500;
  padding: 11px 20px;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, opacity 0.12s;
}
.btn-primary {
  background: var(--brand);
  color: #fff;
  border: 0.5px solid var(--brand);
}
.btn-primary:hover:not(:disabled) { background: var(--brand-deep); }
.btn-outline {
  background: transparent;
  color: var(--text);
  border: 0.5px solid var(--border);
}
.btn-outline:hover:not(:disabled) { border-color: var(--brand-light); }
.btn-primary:disabled,
.btn-outline:disabled { opacity: 0.5; cursor: not-allowed; }

.confirm { text-align: center; padding: 60px 20px; }
.confirm-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--brand-soft);
  color: var(--brand);
}
.confirm h2 { font-size: var(--fs-h4); font-weight: 600; color: var(--brand-deep); margin: 0 0 8px; }
.confirm-sub { font-size: var(--fs-body-lg); color: var(--text-secondary); }
</style>
